import { PreviewAudio } from './previewAudio';
import { seekVideo } from './loadImage';

/** Native user-footage playback for the static preview player. */
export class FootagePlayback {
  readonly audio: PreviewAudio;
  private version = 0;
  private requestedTime: number | null = null;
  private seeking: Promise<void> | null = null;
  private seekAbort = new AbortController();
  private disposed = false;

  constructor(
    readonly clips: HTMLVideoElement[],
    private readonly onSeeked?: (time: number) => void,
    audioClips: HTMLVideoElement[] = clips
  ) {
    this.audio = new PreviewAudio(audioClips);
  }

  matches(clips: HTMLVideoElement[]): boolean { return clips.length === this.clips.length && clips.every((clip, i) => clip === this.clips[i]); }

  async play(time: number): Promise<void> {
    this.pause();
    const version = this.version;
    this.audio.activate();
    try {
      await Promise.all(this.clips.map(async clip => {
        clip.currentTime = clampTime(clip, time);
        if (time < clip.duration) await clip.play();
      }));
    } catch (error) {
      if (version !== this.version || this.disposed) return;
      this.pause();
      throw error;
    }
  }

  pause(): void {
    this.version++;
    this.requestedTime = null;
    this.seekAbort.abort();
    this.seekAbort = new AbortController();
    this.clips.forEach(clip => clip.pause());
    this.audio.deactivate();
  }

  seek(time: number): Promise<void> {
    if (this.disposed) return Promise.resolve();
    this.requestedTime = time;
    if (!this.seeking) {
      this.seeking = this.drainSeeks().finally(() => {
        this.seeking = null;
        if (this.requestedTime !== null && !this.disposed) void this.seek(this.requestedTime).catch(() => {});
      });
    }
    return this.seeking;
  }

  private async drainSeeks(): Promise<void> {
    while (this.requestedTime !== null && !this.disposed) {
      const time = this.requestedTime;
      const signal = this.seekAbort.signal;
      this.requestedTime = null;
      try {
        await Promise.all(this.clips.map(clip => seekVideo(clip, time, { signal })));
        if (!signal.aborted && !this.disposed) this.onSeeked?.(time);
      }
      catch (error) { if (!signal.aborted) throw error; }
    }
  }

  dispose(): void { this.disposed = true; this.pause(); this.audio.dispose(); }
}

function clampTime(clip: HTMLVideoElement, time: number): number {
  return Math.min(Math.max(0, time), Math.max(0, clip.duration - 1 / 120));
}
